import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
/**
 * Parse simple markdown text and return an array of segments
 */
function parseMarkdown(text) {
    // Process patterns in order of precedence to avoid conflicts
    // Bold (**) must be processed before italic (*) 
    const patterns = [
        { type: "bold", regex: /\*\*([^*]+)\*\*/g, placeholder: "BOLD_PLACEHOLDER" },
        { type: "underline", regex: /__([^_]+)__/g, placeholder: "UNDERLINE_PLACEHOLDER" },
        { type: "strikethrough", regex: /~~([^~]+)~~/g, placeholder: "STRIKETHROUGH_PLACEHOLDER" },
        { type: "italic", regex: /\*([^*]+)\*/g, placeholder: "ITALIC_PLACEHOLDER" },
    ];
    let processedText = text;
    const replacements = [];
    // Process each pattern type sequentially
    patterns.forEach(({ type, regex, placeholder }) => {
        let match;
        let counter = 0;
        while ((match = regex.exec(processedText)) !== null) {
            if (match[1]) {
                const uniquePlaceholder = `${placeholder}_${counter}`;
                replacements.push({
                    placeholder: uniquePlaceholder,
                    type,
                    content: match[1],
                });
                // Replace the match with a placeholder
                processedText = processedText.substring(0, match.index) +
                    uniquePlaceholder +
                    processedText.substring(match.index + match[0].length);
                // Reset regex to start from beginning since we modified the string
                regex.lastIndex = 0;
                counter++;
            }
        }
    });
    // Split by placeholders and build segments
    const segments = [];
    let remainingText = processedText;
    // Find all placeholders and their positions
    const placeholderMatches = [];
    replacements.forEach(replacement => {
        const index = remainingText.indexOf(replacement.placeholder);
        if (index !== -1) {
            placeholderMatches.push({
                start: index,
                end: index + replacement.placeholder.length,
                replacement,
            });
        }
    });
    // Sort by position
    placeholderMatches.sort((a, b) => a.start - b.start);
    let currentPos = 0;
    placeholderMatches.forEach(({ start, end, replacement }) => {
        // Add text before placeholder
        if (currentPos < start) {
            const textBefore = remainingText.substring(currentPos, start);
            if (textBefore) {
                segments.push({ type: "text", content: textBefore });
            }
        }
        // Add the formatted segment
        segments.push({
            type: replacement.type,
            content: replacement.content,
        });
        currentPos = end;
    });
    // Add remaining text
    if (currentPos < remainingText.length) {
        const remainingStr = remainingText.substring(currentPos);
        if (remainingStr) {
            segments.push({ type: "text", content: remainingStr });
        }
    }
    return segments;
}
/**
 * Render a markdown segment as a React element
 */
function renderSegment(segment, index) {
    const { type, content } = segment;
    switch (type) {
        case "bold":
            return _jsx("strong", { children: content }, index);
        case "italic":
            return _jsx("em", { children: content }, index);
        case "underline":
            return _jsx("u", { children: content }, index);
        case "strikethrough":
            return _jsx("s", { children: content }, index);
        case "text":
        default:
            // Handle line breaks in text segments
            const lines = content.split('\n');
            if (lines.length === 1) {
                return content;
            }
            return lines.map((line, lineIndex) => (_jsxs(React.Fragment, { children: [line, lineIndex < lines.length - 1 && _jsx("br", {})] }, `${index}-${lineIndex}`)));
    }
}
/**
 * Render simple markdown text as React elements
 * Supports: **bold**, *italic*, __underline__, ~~strikethrough~~, and line breaks
 */
export function renderMarkdown(text) {
    if (!text)
        return null;
    const segments = parseMarkdown(text);
    if (segments.length === 0) {
        return text;
    }
    return segments.map((segment, index) => renderSegment(segment, index));
}
